import { createSelector } from 'reselect';

import {
  DEFAULT_HELP_VIEW_FOOTER_LINK_KNOWLEDGE_BASE_URL,
  DEFAULT_HELP_VIEW_FOOTER_LINK_MOBILE_APP_URL,
  DEFAULT_HELP_VIEW_FOOTER_LINK_SUPPORT_URL,
  DEFAULT_HELP_VIEW_FOOTER_LINK_VIDEOS_URL,
  DEFAULT_MAX_CALLABLES_PER_FLOW_COUNT,
  DEFAULT_MAX_CONSTANTS_PER_WORKFLOW,
  DEFAULT_MAX_IMAGE_UPLOAD_COUNT,
  DEFAULT_MAX_IMAGE_UPLOAD_SIZE_KB,
  DEFAULT_MAX_ONCALL_GROUP_SHIFT_MEMBERSHIP,
  DEFAULT_MAX_SERVICE_DEPENDENCIES,
  DEFAULT_MAX_SERVICES,
  DEFAULT_MAX_SERVICES_BEFORE_WARNING,
  DEFAULT_INCIDENT_INSIGHT_SIMILAR_INCIDENT_POPUP_THRESHOLD,
  DEFAULT_INCIDENT_INSIGHT_RESOLVER_SCORE_THRESHOLD,
  DEFAULT_INCIDENT_INSIGHT_RESOLVER_SCORE_POPUP_THRESHOLD,
} from 'xm-constants/customerConfig';
import { isEverbridgeFlowDesignerProduct } from 'utils/products';
import { selectFeatureToggleMap } from 'selectors/navigation/util';

import { isEBSEmbedded } from '@xmatters/common/context/ebsGlobalContext';
import {
  createSelectorForFlag,
  createSelectorForNumberSetting,
  createSelectorForTextSetting,
} from './util';

export const featureToggleRequestStatus = state => state.featureToggles.status;

export const isEditIncidentsPermissionsEnabled = createSelectorForFlag(
  'EDIT_INCIDENT_PERMISSION_AND_FUNCTION',
);

export const getMaxImageCount = createSelectorForNumberSetting(
  'MAX_IMAGE_UPLOAD_COUNT',
  DEFAULT_MAX_IMAGE_UPLOAD_COUNT,
);

export const getSimilarIncidentPopupThreshold = createSelectorForNumberSetting(
  'INCIDENT_INSIGHT_SIMILAR_INCIDENT_POPUP_THRESHOLD',
  DEFAULT_INCIDENT_INSIGHT_SIMILAR_INCIDENT_POPUP_THRESHOLD,
);

export const getResolverScoreThreshold = createSelectorForNumberSetting(
  'INCIDENT_INSIGHT_RESOLVER_SCORE_THRESHOLD',
  DEFAULT_INCIDENT_INSIGHT_RESOLVER_SCORE_THRESHOLD,
);

export const getResolverScorePopupThreshold = createSelectorForNumberSetting(
  'INCIDENT_INSIGHT_RESOLVER_SCORE_POPUP_THRESHOLD',
  DEFAULT_INCIDENT_INSIGHT_RESOLVER_SCORE_POPUP_THRESHOLD,
);

export const getMaxImageSizeKB = createSelectorForNumberSetting(
  'MAX_IMAGE_UPLOAD_SIZE_KB',
  DEFAULT_MAX_IMAGE_UPLOAD_SIZE_KB,
);

export const maxConstantsAllowedInWorkflow = createSelectorForNumberSetting(
  'MAX_CONSTANTS_PER_WORKFLOW',
  DEFAULT_MAX_CONSTANTS_PER_WORKFLOW,
  true,
);

export const getAllowedCallablesInFlow = createSelectorForNumberSetting(
  'MAX_CALLABLES_PER_FLOW_COUNT',
  DEFAULT_MAX_CALLABLES_PER_FLOW_COUNT,
  true,
);

export const isConferenceBridgeTabInCreateAlertFlowBlockEnabled = createSelectorForFlag(
  'CONF_BRIDGE_TAB_IN_CREATE_ALERT_STEP',
);

export const isConferenceBridgeTabInCreateEventFlowBlockEnabled = createSelectorForFlag(
  'CONF_BRIDGE_TAB_IN_CREATE_EVENT_STEP',
);

export const isAnnualRenewalProductPlansEnabled = createSelectorForFlag(
  'ENABLE_ANNUAL_RENEWAL_PRODUCT_PLANS',
);

export const getIsQuickStartGuideEnabled = createSelectorForFlag(
  'FREE_SUPERVISOR_QUICKSTART_GUIDE',
);

export const isPendoGuideEnabled = createSelectorForFlag(
  'INTEGRATE_PENDO_USER_GUIDES',
);

export const incidentManagementMVPEnabled = createSelectorForFlag(
  'INCIDENT_MANAGEMENT_MVP',
);

export const isAdminAccountUpgradeEnabled = createSelectorForFlag(
  'ENABLE_ADMIN_ACCOUNT_UPGRADE',
);

export const isAccountSettingsEnabled = createSelectorForFlag(
  'ENABLE_ACCOUNT_SETTINGS',
);

export const isInviteYourTeamEnabled = createSelectorForFlag(
  'ALLOW_INVITATIONS',
);

export const isUserIntentPromptEnabled = createSelectorForFlag(
  'ENABLE_REQUEST_FOR_SUPPORT_PROMPT',
);

export const isOverProvisionQuotaSupportEnabled = createSelectorForFlag(
  'OVER_PROVISION_QUOTA_ENTITLEMENT',
);

export const privilegedDevicesEnabled = createSelectorForFlag(
  'PRIVILEGED_DEVICES',
);

export const getProductTier = createSelector(
  [selectFeatureToggleMap],
  featureToggleMap => featureToggleMap.PRODUCT_TIER,
);

export const getIsOnFreePlan = createSelector(
  [getProductTier],
  productTier => productTier === 'FREE',
);

export const getIsOnPaidPlan = createSelector(
  [getProductTier],
  productTier => productTier === 'PAID',
);

export const getIsPremiumVoiceFeatureEnabled = createSelectorForFlag(
  'ENABLE_PREMIUM_VOICE_FEATURE',
);

export const getWorkflowsGlobalEmailCSS = createSelector(
  [selectFeatureToggleMap],
  featureToggleMap => {
    return featureToggleMap.APPLICATIONSEMAILCSS;
  },
);

export const getMaxNumberIncidentAttachments = createSelector(
  [selectFeatureToggleMap],
  featureToggleMap => {
    return Number(featureToggleMap.MAX_NUM_INCIDENT_ATTACHMENTS);
  },
);

export const getMaxAttachmentDocumentSize = createSelector(
  [selectFeatureToggleMap],
  featureToggleMap => {
    return Number(featureToggleMap.MAX_ATTACHMENT_DOCUMENT_SIZE_MB);
  },
);

export const getMaxNumberDocuments = createSelector(
  [selectFeatureToggleMap],
  featureToggleMap => {
    return Number(featureToggleMap.MAX_NUM_DOCUMENTS);
  },
);

export const isDocumentLibraryAvailableBasedOnPlan = createSelector(
  [getMaxNumberDocuments, getMaxAttachmentDocumentSize],
  (maxNumberDocuments, maxAttachmentDocumentSize) => {
    return maxNumberDocuments && maxAttachmentDocumentSize;
  },
);

export const getIsOnTrialPlan = createSelector(
  [getProductTier],
  productTier => productTier === 'TRIAL',
);

export const getIsOnFreeOrTrial = createSelector(
  [getProductTier],
  productTier => ['FREE', 'TRIAL'].includes(productTier),
);

export const showTriggerFloodControl = createSelectorForFlag(
  'TRIGGER_FLOOD_CONTROL',
);

export const isCreatePostIncidentReportsEnabled = createSelectorForFlag(
  'CREATE_POST_INCIDENT_REPORTS',
);

export const getGoogleApi = createSelector(
  [selectFeatureToggleMap],
  featureToggleMap => {
    return featureToggleMap.GOOGLE_MAP_API_URL;
  },
);

export const isIncidentResolverRolesEnabled = createSelectorForFlag(
  'INCIDENT_RESOLVER_ROLES',
);

export const isIncidentResolversOrderEnabled = createSelectorForFlag(
  'INCIDENT_RESOLVERS_ORDERING',
);

export const isIncidentStakeholderEnabled = createSelectorForFlag(
  'INCIDENT_STAKEHOLDERS',
);

export const isIncidentAutomationEnabled = createSelectorForFlag(
  'INCIDENT_AUTOMATION_ACTIONS',
);

export const voiceCreditsAvailable = createSelector(
  [selectFeatureToggleMap],
  featureToggleMap =>
    featureToggleMap.TWILIO_SUBACCOUNT_VOICE_USAGE_AVAILABLE &&
    Number(featureToggleMap.TWILIO_SUBACCOUNT_VOICE_USAGE_AVAILABLE),
);

export const voiceCreditPercentage = createSelector(
  [selectFeatureToggleMap],
  ({
    TWILIO_SUBACCOUNT_VOICE_USAGE_AVAILABLE,
    TWILIO_SUBACCOUNT_VOICE_USAGE_REMAINING,
  }) =>
    TWILIO_SUBACCOUNT_VOICE_USAGE_AVAILABLE &&
    (TWILIO_SUBACCOUNT_VOICE_USAGE_REMAINING /
      TWILIO_SUBACCOUNT_VOICE_USAGE_AVAILABLE) *
      100,
);

export const isReactUserProfileEnabled = createSelectorForFlag(
  'REACT_USER_PROFILE_MVP',
);

export const isNotificationFilteringServiceEnabled = createSelectorForFlag(
  'NOTIFICATION_FILTERING_SERVICE',
);
export const isFloodControlEnabled = createSelector(
  [isNotificationFilteringServiceEnabled],
  notificationFilteringServiceEnabled => notificationFilteringServiceEnabled,
);

export const isEverbridgeBrandingEnabled = createSelectorForFlag(
  'ENABLE_EB_PRODUCT_BRANDING',
);

export const isWebUiIconsDisabled = () => isEBSEmbedded();

export const getUncachedUrl = createSelector(
  [isEverbridgeBrandingEnabled],
  isUpdateEnabled => url => `${url}?${isUpdateEnabled ? '2' : '1'}`,
);

export const isStakeholderUsersEnabled = createSelectorForFlag(
  'ENABLE_STAKEHOLDER_USERS',
);

export const getHelpViewFooterLinkKnowledgeBaseUrl = createSelectorForTextSetting(
  'HELP_VIEW_FOOTER_LINK_KNOWLEDGE_BASE_URL',
  DEFAULT_HELP_VIEW_FOOTER_LINK_KNOWLEDGE_BASE_URL,
);

export const getHelpViewFooterLinkMobileAppUrl = createSelectorForTextSetting(
  'HELP_VIEW_FOOTER_LINK_MOBILE_APP_URL',
  DEFAULT_HELP_VIEW_FOOTER_LINK_MOBILE_APP_URL,
);

export const getHelpViewFooterLinkSupportUrl = createSelectorForTextSetting(
  'HELP_VIEW_FOOTER_LINK_SUPPORT_URL',
  DEFAULT_HELP_VIEW_FOOTER_LINK_SUPPORT_URL,
);

export const getHelpViewFooterLinkVideosUrl = createSelectorForTextSetting(
  'HELP_VIEW_FOOTER_LINK_VIDEOS_URL',
  DEFAULT_HELP_VIEW_FOOTER_LINK_VIDEOS_URL,
);

export const isServicesEnabled = createSelectorForFlag(
  'SERVICES_LIST_AND_DETAILS',
);

export const isServicesPropertyInFlowTriggerForms = createSelectorForFlag(
  'SERVICES_PROPERTY_IN_FLOW_TRIGGER_FORMS',
);

// Combines two feature toggles into one selector:
export const canUseFlowTriggerServicesProperty = state =>
  isServicesEnabled(state) && isServicesPropertyInFlowTriggerForms(state);

export const isMsTeamsDeviceMvpEnabled = createSelectorForFlag(
  'MS_TEAMS_DEVICE_MVP',
);

export const isSlackDeviceMvpEnabled = createSelectorForFlag(
  'SLACK_DEVICE_MVP',
);

export const isIncidentChangeTriggersEnabled = createSelectorForFlag(
  'XMATTERS_INCIDENT_CHANGE_TRIGGERS',
);

export const isZoomCollaborationChannelEnabled = createSelectorForFlag(
  'INCIDENT_ZOOM_COLLABORATION_CHANNEL',
);

export const reactUserRolesDialogEnabled = createSelectorForFlag(
  'REACT_USER_ROLES_DIALOG',
);

export const getIsEmailTemplateResponseOptionsEnabled = createSelectorForFlag(
  'EMAIL_TEMPLATE_RESPONSE_OPTIONS',
);

export const isShowLicenseQuotasEnabled = createSelectorForFlag(
  'SHOW_LICENSE_QUOTAS',
);

export const isLicenseQuotaEnforced = createSelectorForFlag(
  'ENFORCE_USER_QUOTAS',
);

export const isCallableFlowEnabled = createSelectorForFlag('CALLABLE_FLOW_MVP');

export const isTempAbsenceDirectNotifyEnabled = createSelectorForFlag(
  'ENABLE_ABSENCE_DIRECT_SETTING',
);

export const isDevicesEnabled = createSelectorForFlag(
  'REACT_USER_PROFILE_DEVICES',
);

export const getOptimizeWelcomeGuideExperimentId = createSelector(
  [selectFeatureToggleMap],
  featureToggleMap => featureToggleMap.OPTIMIZE_WELCOME_GUIDE_EXPERIMENT_ID,
);

export const responseOptionDependentFeaturesEnabled = createSelectorForFlag(
  'ENABLE_RESPONSE_OPTION_DEPENDENT_FEATURES',
);

export const newUserDeliveryTabEnabled = createSelectorForFlag(
  'NEW_USER_DELIVERY_TAB',
);

export const isAlertReportSMSMessageEnabled = createSelectorForFlag(
  'ALERT_REPORT_SMS_MESSAGE',
);

export const isGroupUserIncidentsPerformanceReportsEnabled = createSelectorForFlag(
  'GROUP_USER_INCIDENT_PERFORMANCE',
);

export const isIncidentPerformanceGroupCountChartEnabled = createSelectorForFlag(
  'INCIDENT_PERFORMANCE_GROUP_COUNT_CHART',
);

export const isIncidentPerformanceGroupMTTRChartEnabled = createSelectorForFlag(
  'INCIDENT_PERFORMANCE_GROUP_MTTR_CHART',
);

export const isIncidentPerformanceGroupMTTAChartEnabled = createSelectorForFlag(
  'INCIDENT_PERFORMANCE_GROUP_MTTA_CHART',
);

export const isIncidentPerformanceGroupChartsEnabled = createSelector(
  [
    isIncidentPerformanceGroupCountChartEnabled,
    isIncidentPerformanceGroupMTTRChartEnabled,
    isIncidentPerformanceGroupMTTAChartEnabled,
  ],
  (
    incidentPerformanceGroupCountChartEnabled,
    incidentPerformanceGroupMTTRChartEnabled,
    incidentPerformanceGroupMTTAChartEnabled,
  ) =>
    incidentPerformanceGroupCountChartEnabled ||
    incidentPerformanceGroupMTTRChartEnabled ||
    incidentPerformanceGroupMTTAChartEnabled,
);

export const isIncidentPerformanceUserCountChartEnabled = createSelectorForFlag(
  'INCIDENT_PERFORMANCE_USER_COUNT_CHART',
);

export const isIncidentPerformanceUserMTTRChartEnabled = createSelectorForFlag(
  'INCIDENT_PERFORMANCE_USER_MTTR_CHART',
);

export const isIncidentPerformanceUserMTTAChartEnabled = createSelectorForFlag(
  'INCIDENT_PERFORMANCE_USER_MTTA_CHART',
);

export const isIncidentPerformanceUserChartsEnabled = createSelector(
  [
    isIncidentPerformanceUserCountChartEnabled,
    isIncidentPerformanceUserMTTRChartEnabled,
    isIncidentPerformanceUserMTTAChartEnabled,
  ],
  (
    incidentPerformanceUserCountChartEnabled,
    incidentPerformanceUserMTTRChartEnabled,
    incidentPerformanceUserMTTAChartEnabled,
  ) =>
    incidentPerformanceUserCountChartEnabled ||
    incidentPerformanceUserMTTRChartEnabled ||
    incidentPerformanceUserMTTAChartEnabled,
);

export const isAfterHoursAlertsEnabled = createSelectorForFlag(
  'USER_PERFORMANCE_AFTER_HOURS_ALERTS',
);

export const isReactChangePasswordEnabled = createSelectorForFlag(
  'REACT_USER_PROFILE_CHANGE_PASSWORD',
);

export const isFlowDesignerReactFlow = createSelectorForFlag(
  'ENABLE_FLOW_DESIGNER_REACT_FLOW_RENDERER',
);

export const getMaxServices = createSelectorForNumberSetting(
  'MAX_SERVICES',
  DEFAULT_MAX_SERVICES,
);

export const getMaxServiceDependencies = createSelectorForNumberSetting(
  'MAX_SERVICES_DEPENDENCIES',
  DEFAULT_MAX_SERVICE_DEPENDENCIES,
);

export const getGraphLimitServices = createSelectorForNumberSetting(
  'MAX_SERVICES_BEFORE_WARNING',
  DEFAULT_MAX_SERVICES_BEFORE_WARNING,
);

export const isBroadcastGroupsEnabled = createSelectorForFlag(
  'BROADCAST_GROUPS',
);

export const isFlowDesignerDragToConnectEnabled = createSelectorForFlag(
  'ENABLE_FLOW_DESIGNER_DRAG_TO_CONNECT',
);

export const isNewDateTimeUXEnabled = createSelectorForFlag(
  'REPORTING_DATETIME_UX',
);

export const getHistoricalDataAccessLimit = createSelector(
  [selectFeatureToggleMap],
  featureToggleMap =>
    parseInt(featureToggleMap?.HISTORICAL_DATA_ACCESS_LIMIT ?? -1, 10),
);

export const isCreateAutomationEnabled = createSelectorForFlag(
  'CREATE_INCIDENT_AUTOMATIONS',
);

export const isFDEdgeManipulationActionsEnabled = createSelectorForFlag(
  'ENABLE_FD_EDGE_ACTIONS',
);

export const isIncidentTriggerDetailsEnabled = createSelectorForFlag(
  'ENABLE_INCIDENT_TRIGGER_DETAILS',
);

export const isNotifyToEngageIncidentTriggerEnabled = createSelectorForFlag(
  'ENABLE_NOTIFY_TO_ENGAGE_INCIDENT_TRIGGER',
);

export const isServiceChangesEnabled = createSelectorForFlag('SERVICE_CHANGES');

export const isServicesAsRecipientsEnabled = createSelectorForFlag(
  'SERVICES_AS_RECIPIENTS_ON_FORMS',
);

export const isReactDynamicGroupsUIEnabled = createSelectorForFlag(
  'REACT_DYNAMIC_GROUPS_UI',
);

export const isChangeIntelligenceEnabled = createSelectorForFlag(
  'CONSOLE_CHANGE_INTELLIGENCE',
);

export const isReactOnCallGroupOverviewEnabled = createSelectorForFlag(
  'REACT_ONCALL_GROUP_OVERVIEW',
);

export const isIncidentInsightsEnabled = createSelectorForFlag(
  'INCIDENT_INSIGHTS',
);

export const isInsightsPopupsEnabled = createSelectorForFlag('INSIGHTS_POPUPS');

export const isIncidentLinksEnabled = createSelectorForFlag('INCIDENT_LINKS');

export const isNewReactOAuthTokensPageEnabled = createSelectorForFlag(
  'REACT_OAUTH_TOKENS_PAGE',
);
export const isWorkflowTemplatesOrganisationEnabled = createSelectorForFlag(
  'WORKFLOW_TEMPLATES_ORGANIZATION',
);

export const getProductName = createSelector(
  [selectFeatureToggleMap],
  featureToggleMap => featureToggleMap.PRODUCT_NAME ?? '',
);

export const isEverbridgeFlowDesignerApp = createSelector(
  [getProductName],
  productName => isEverbridgeFlowDesignerProduct(productName),
);

// embedded toggles. Separated to simplify turning individual ones on in future
export const isHelpLinksDisabled = () => isEBSEmbedded();
export const isIncidentTimelineLinksDisabled = () => isEBSEmbedded();
export const isUserInfoLinksDisabled = () => isEBSEmbedded();
export const isDocumentLibrarySupported = () => !isEBSEmbedded();

export const isWorkflowTemplateSupportEnabled = state =>
  !isEverbridgeFlowDesignerApp(state);

export const isXmattersEndpointSupportEnabled = state =>
  !isEverbridgeFlowDesignerApp(state);

export const isXmattersFlowDesignerToolsEnabled = state =>
  !isEverbridgeFlowDesignerApp(state);

export const isSupportForGroupAndRoleRecipientsEnabled = state =>
  !isEverbridgeFlowDesignerApp(state);

export const isXmattersEmailAuthSupportEnabled = state =>
  !isEverbridgeFlowDesignerApp(state);

export const isMinimalWorkflowEnabled = state =>
  isEverbridgeFlowDesignerApp(state);

export const isManageSharedAutomationsControlEnabled = state =>
  !isEverbridgeFlowDesignerApp(state);

export const isNotificationFloodControlEnabled = state =>
  !isEverbridgeFlowDesignerApp(state);

export const isApiKeyAuthenticationEnabled = state =>
  !isEverbridgeFlowDesignerApp(state);

export const isOAuthAuthenticationEnabled = state =>
  !isEverbridgeFlowDesignerApp(state);

export const isNavigationSubMenuTitlesEnabled = state =>
  !isEverbridgeFlowDesignerApp(state);

export const isWorkflowImportLogFormsEnabled = state =>
  !isEverbridgeFlowDesignerApp(state);

export const isXAgentMonitorsEnabled = state =>
  !isEverbridgeFlowDesignerApp(state);

export const showGenericImage = state => isEverbridgeFlowDesignerApp(state);

export const isDynamicTeamsDisabled = () => isEBSEmbedded();
export const isXMConferenceDisabled = () => isEBSEmbedded();

export const getMaxShiftMembers = createSelectorForNumberSetting(
  'MAX_ONCALL_GROUP_SHIFT_MEMBERSHIP',
  DEFAULT_MAX_ONCALL_GROUP_SHIFT_MEMBERSHIP,
  true,
);

export const isVoiceRecordingListPageEnabled = createSelectorForFlag(
  'REACT_VOICE_RECORDINGS_PAGE',
);

export const isTtsDictionaryEnabled = createSelectorForFlag(
  'REACT_TTS_DICTIONARY',
);

export const isQrLoginWarningEnabled = createSelectorForFlag('QR_WARNING');

export const isNewDefaultAlertsView = createSelectorForFlag(
  'DEFAULT_ALERTS_VIEWS',
);

export const isReactDynamicTeamDetailsPageEnabled = createSelectorForFlag(
  'REACT_DYNAMIC_TEAMS',
);

export const isNotificationsByDeviceTypeChartEnabled = createSelectorForFlag(
  'NOTIFICATIONS_BY_DEVICE_TYPE_CHART',
);

export const isNotificationsBySourceChartEnabled = createSelectorForFlag(
  'NOTIFICATIONS_BY_SOURCE_CHART',
);

export const isOAuth2EndpointAuthCodeEnabled = createSelectorForFlag(
  'ENDPOINT_OAUTH2_AUTH_CODE_FLOW',
);

export const isServiceNowOauthEndpointEnabled = createSelectorForFlag(
  'ENDPOINT_SERVICENOW_OAUTH',
);

export const isIncidentAttachmentsEnabled = createSelectorForFlag(
  'INCIDENT_ATTACHMENTS',
);

export const isDocumentLibraryEnabled = createSelectorForFlag(
  'DOCUMENT_LIBRARY',
);

export const isDocumentHierarchyEnabled = createSelectorForFlag(
  'DOCUMENT_HIERARCHY',
);

export const isIncidentTasksEnabled = createSelectorForFlag('INCIDENT_TASKS');

export const isIncidentTaskListsEnabled = createSelectorForFlag(
  'INCIDENT_TASK_LISTS',
);

export const isIncidentTypesEnabled = createSelectorForFlag('INCIDENT_TYPES');

export const isIncidentPropertiesV2Enabled = createSelectorForFlag(
  'INCIDENT_PROPERTIES_V2',
);

export const isIncidentPropertiesGetIncidentEnabled = createSelectorForFlag(
  'INCIDENT_PROPERTIES_FOR_GET_INCIDENT_STEP',
);

export const isIncidentPropertiesUpdateIncidentEnabled = createSelectorForFlag(
  'INCIDENT_PROPERTIES_FOR_UPDATE_INCIDENT_STEP',
);

export const getWebServerUrl = createSelectorForTextSetting('WEBSERVERURL');

// This integrationProxyUrl is referring to what is called the "slackProxy"
// but slackProxy has evolved to support many more 3rd parties than just slack without being renamed
// This comment is important because 1 day, there will be a new service called "integration proxy"
// serving a different purpose, and we'll want to know this here is not referring to that then.
export const getIntegrationProxyUrl = createSelectorForTextSetting(
  'INTEGRATION_PROXY_URL',
  '',
);

export const isServiceTypesEnabled = createSelectorForFlag('SERVICE_TYPES');

export const isConditionStepEnabled = createSelectorForFlag(
  'STEP_FD_CONDITION',
);

export const isConcurrentSessionsDeniedEnabled = createSelectorForFlag(
  'WEBUI_CONCURRENT_SESSIONS_DENIED',
);

// Bit of weird naming - the flag is DISABLED instead of ENABLED
export const isAddingIncidentTypesDisabled = createSelectorForFlag(
  'DISABLE_ADDING_INCIDENT_TYPES',
);

export const isWebOcrEnabled = createSelectorForFlag('OCR_WEB');

export const getUserDeliveryExportLimit = createSelector(
  [selectFeatureToggleMap],
  featureToggleMap =>
    parseInt(featureToggleMap?.USER_DELIVERY_EXPORT_LIMIT ?? 1000, 10),
);

export const getUserDeliveryTableLimit = createSelector(
  [selectFeatureToggleMap],
  featureToggleMap =>
    parseInt(featureToggleMap?.USER_DELIVERY_TABLE_LIMIT ?? 100, 10),
);

// active alert polling interval in ms
export const getActiveAlertsPollingInterval = createSelector(
  [selectFeatureToggleMap],
  featureToggleMap =>
    (parseInt(featureToggleMap?.ACTIVE_ALERTS_POLLING_INTERVAL ?? 0, 10) || 0) *
    1000,
);

export const isUserAuditReportEnabled = createSelectorForFlag(
  'USER_AUDIT_REPORT',
);
